import { useState } from "react";
import { Mail, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";

export default function Contact() {
  useSeo({ title: "Contact", description: "Get in touch with the Wolfdog News editorial team." });
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! Our team will be in touch.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Could not send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-14">
      <p className="overline text-[#8B6D1F]">Get in Touch</p>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#0A192F] mt-1">Contact Us</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-12">
        <div className="space-y-6">
          <p className="text-[#4A5568] leading-relaxed">
            Have a news tip, story idea, correction, or partnership inquiry? We'd love to hear from you.
          </p>
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-[#D4AF37] mt-1" />
            <div>
              <p className="overline text-[#0A192F]">Editorial Desk</p>
              <p className="text-sm text-[#4A5568]">editor@wolfdognews.com</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-[#D4AF37] mt-1" />
            <div>
              <p className="overline text-[#0A192F]">Newsroom</p>
              <p className="text-sm text-[#4A5568]">United States</p>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-2 bg-white border border-[#0A192F]/10 p-8 space-y-4" data-testid="contact-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input value={form.name} onChange={set("name")} placeholder="Your name" data-testid="contact-name" className="px-4 py-3 border border-[#0A192F]/20 outline-none focus:ring-2 focus:ring-[#D4AF37]" />
            <input value={form.email} onChange={set("email")} type="email" placeholder="Email address" data-testid="contact-email" className="px-4 py-3 border border-[#0A192F]/20 outline-none focus:ring-2 focus:ring-[#D4AF37]" />
          </div>
          <input value={form.subject} onChange={set("subject")} placeholder="Subject" data-testid="contact-subject" className="w-full px-4 py-3 border border-[#0A192F]/20 outline-none focus:ring-2 focus:ring-[#D4AF37]" />
          <textarea value={form.message} onChange={set("message")} placeholder="Your message" rows={6} data-testid="contact-message" className="w-full px-4 py-3 border border-[#0A192F]/20 outline-none focus:ring-2 focus:ring-[#D4AF37]" />
          <button type="submit" disabled={loading} data-testid="contact-submit" className="inline-flex items-center gap-2 bg-[#0A192F] text-white overline px-7 py-3 hover:bg-[#0A192F]/90 transition-colors disabled:opacity-60">
            <Send className="w-4 h-4" /> {loading ? "Sending..." : "Send Message"}
          </button>
        </form>
      </div>
    </div>
  );
}
